import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertTriangle, ArrowRight, Banknote } from 'lucide-react';
import AppLayout from '../components/layout/AppLayout';
import { useTimezone, shiftStatus, PAGE, SHIFT } from '@kioskgaming/ui';
import { formatInTimezone } from '@kioskgaming/ui';
import NoShowActionsPanel from '../components/shifts/NoShowActionsPanel';
import PortalEmptyState from '../components/ui/PortalEmptyState';
import ModalPortal from '../components/ui/ModalPortal';
import {
  confirmCashDrop,
  fetchCashDrops,
  fetchCashierDevices,
  fetchCashierExceptions,
  fetchShopCashiers,
  rejectCashDrop,
} from '../services/api';
import type { CashierCashDrop, CashierDevice, CashierShift, ShopCashier } from '../types';
import { scheduleShiftPath } from '../utils/cashierSchedule';

function shiftStatusClass(status: string): string {
  if (status === 'no_show') return 'border-rose-200 bg-rose-50 text-rose-800';
  if (status === 'suspended' || status === 'auto_locked') return 'border-amber-200 bg-amber-50 text-amber-800';
  if (status === 'pending_manager_review') return 'border-orange-200 bg-orange-50 text-orange-800';
  return 'border-stone-200 bg-stone-50 text-stone-700';
}

export default function CashierExceptionsPage() {
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const [exceptions, setExceptions] = useState<CashierShift[]>([]);
  const [cashDrops, setCashDrops] = useState<CashierCashDrop[]>([]);
  const [cashiers, setCashiers] = useState<ShopCashier[]>([]);
  const [devices, setDevices] = useState<CashierDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [noShowTarget, setNoShowTarget] = useState<CashierShift | null>(null);

  const getApiErrorMessage = (e: unknown) => {
    const anyE = e as { response?: { data?: { message?: string } }; message?: string };
    return anyE?.response?.data?.message || anyE?.message || null;
  };

  const cashierNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cashiers) map.set(c.id, c.name);
    return map;
  }, [cashiers]);

  const load = useCallback(async () => {
    setLoading(true);
    // Settled separately: a failed side list (drops, cashiers, devices) must not blank the exceptions.
    const [excResult, dropResult, cashierResult, deviceResult] = await Promise.allSettled([
      fetchCashierExceptions(),
      fetchCashDrops({ status: 'pending_receiver_confirmation' }),
      fetchShopCashiers(),
      fetchCashierDevices(),
    ]);
    try {
      if (excResult.status === 'rejected') throw excResult.reason;
      const excRes = excResult.value;
      if (!excRes.success) throw new Error(excRes.message || 'Failed to load exceptions');
      setExceptions(excRes.data || []);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Failed to load exceptions');
      setExceptions([]);
    }
    if (dropResult.status === 'fulfilled' && dropResult.value.success) setCashDrops(dropResult.value.data || []);
    if (cashierResult.status === 'fulfilled' && cashierResult.value.success) {
      setCashiers(cashierResult.value.data || []);
    }
    if (deviceResult.status === 'fulfilled' && deviceResult.value.success) {
      setDevices(deviceResult.value.data || []);
    } else {
      toast.error('Devices could not be loaded. No-show actions will offer “Any approved device” only.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onConfirmDrop = async (drop: CashierCashDrop) => {
    setActionId(drop.id);
    try {
      const res = await confirmCashDrop(drop.id);
      if (!res.success) throw new Error(res.message || 'Confirm failed');
      toast.success('Cash drop confirmed');
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Confirm failed');
    } finally {
      setActionId(null);
    }
  };

  const onRejectDrop = async (drop: CashierCashDrop) => {
    const note = window.prompt('Rejection reason (optional):') ?? '';
    setActionId(drop.id);
    try {
      const res = await rejectCashDrop(drop.id, note.trim() ? { note: note.trim() } : undefined);
      if (!res.success) throw new Error(res.message || 'Reject failed');
      toast.success('Cash drop rejected');
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Reject failed');
    } finally {
      setActionId(null);
    }
  };

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{PAGE.shiftExceptions}</h1>
            <p className="mt-0.5 text-sm text-stone-600">
              Shifts that need your attention, and cash drops waiting for you to confirm.
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
            onClick={() => void load()}
          >
            Refresh
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              Shift exceptions
            </div>
            <div className="mt-1 text-2xl font-bold text-amber-900">{exceptions.length}</div>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-blue-700">
              <Banknote className="h-4 w-4" />
              Pending cash drops
            </div>
            <div className="mt-1 text-2xl font-bold text-blue-900">{cashDrops.length}</div>
          </div>
        </div>

        {cashDrops.length > 0 && (
          <div className="mt-6 rounded-xl border border-stone-200 bg-white">
            <div className="border-b border-stone-200 px-4 py-3">
              <h2 className="text-base font-semibold">Pending cash drops</h2>
              <p className="text-xs text-stone-500">Confirm or reject cash drops from active shifts.</p>
            </div>
            <div className="divide-y divide-stone-100">
              {cashDrops.map((drop) => (
                <div key={drop.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-stone-900">
                      ${Number(drop.totalAmount).toFixed(2)}
                    </div>
                    <div className="mt-0.5 text-xs text-stone-600">
                      {cashierNameById.get(drop.cashierId) || 'Cashier'} · {formatInTimezone(drop.createdAt, timezone)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={actionId === drop.id}
                      className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                      onClick={() => void onConfirmDrop(drop)}
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      disabled={actionId === drop.id}
                      className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                      onClick={() => void onRejectDrop(drop)}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-semibold hover:bg-stone-50"
                      onClick={() => navigate(`/reconciliation/${encodeURIComponent(drop.shiftId)}`)}
                    >
                      View reconciliation
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 rounded-xl border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-4 py-3">
            <h2 className="text-base font-semibold">Shift exceptions</h2>
            <p className="text-xs text-stone-500">No-shows, suspensions, auto-locks and pending reviews.</p>
          </div>
          {loading ? (
            <div className="px-4 py-8 text-sm text-stone-600">Loading…</div>
          ) : exceptions.length ? (
            <div className="divide-y divide-stone-100">
              {exceptions.map((shift) => (
                <div
                  key={shift.id}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 hover:bg-stone-50"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/shifts/${encodeURIComponent(shift.id)}`)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="truncate text-sm font-semibold text-stone-900">
                      {cashierNameById.get(shift.cashierId) || 'Cashier'}
                    </div>
                    <div className="mt-0.5 text-xs text-stone-500">
                      {shift.shiftDate || shift.scheduledStartAt?.slice(0, 10) || '—'} ·{' '}
                      {formatInTimezone(shift.scheduledStartAt, timezone)} – {formatInTimezone(shift.scheduledEndAt, timezone)}
                    </div>
                  </button>
                  <div className="flex shrink-0 items-center gap-2">
                    <span
                      className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${shiftStatusClass(shift.status)}`}
                    >
                      {shiftStatus(shift.status).label}
                    </span>
                    {shift.status === 'no_show' ? (
                      <button
                        type="button"
                        className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                        onClick={() => setNoShowTarget(shift)}
                      >
                        Handle
                      </button>
                    ) : null}
                    {shift.status === 'auto_locked' ? (
                      <>
                        <button
                          type="button"
                          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                          onClick={() => navigate(`/reconciliation/${encodeURIComponent(shift.id)}`)}
                        >
                          Reconcile
                        </button>
                        <button
                          type="button"
                          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                          onClick={() => navigate(scheduleShiftPath(shift.cashierId))}
                        >
                          {SHIFT.scheduleShift}
                        </button>
                      </>
                    ) : null}
                    <button
                      type="button"
                      className="rounded-lg border border-stone-200 p-1.5 hover:bg-stone-100"
                      onClick={() => navigate(`/shifts/${encodeURIComponent(shift.id)}`)}
                      aria-label="View shift details"
                    >
                      <ArrowRight className="h-4 w-4 text-stone-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <PortalEmptyState
              icon={<AlertTriangle className="mx-auto h-12 w-12 text-stone-400" strokeWidth={1.5} />}
              title="No shift exceptions"
              description="All shifts are running normally. Exceptions will appear here when action is needed."
            />
          )}
        </div>
      </div>

      {noShowTarget ? (
        <ModalPortal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto">
              <NoShowActionsPanel
                shift={noShowTarget}
                cashiers={cashiers}
                devices={devices}
                onReassignSuccess={(updated) => {
                  setNoShowTarget(null);
                  setExceptions((prev) => prev.filter((s) => s.id !== updated.id));
                }}
                onCancelSuccess={() => {
                  setNoShowTarget(null);
                  setExceptions((prev) => prev.filter((s) => s.id !== noShowTarget.id));
                }}
                onReplacementSuccess={(replacement) => {
                  setNoShowTarget(null);
                  setExceptions((prev) => prev.filter((s) => s.id !== noShowTarget.id));
                  navigate(`/shifts/${encodeURIComponent(replacement.id)}`);
                }}
                onCancel={() => setNoShowTarget(null)}
              />
            </div>
          </div>
        </ModalPortal>
      ) : null}
    </AppLayout>
  );
}
